/**
 * Servicio para manejar la API de fechas festivas
 * Proporciona funciones para gestionar días feriados y fechas no laborables
 */

import apiClient from './apiClient';
import { API_ENDPOINTS } from '@/config/api';

/**
 * Obtiene todos los días festivos
 * @returns {Promise<Array>} - Lista de días festivos
 */
export const getFestivos = async () => {
  try {
    //console.log('📅 [FestivosService] Obteniendo lista de festivos...');
    
    const response = await apiClient.get(API_ENDPOINTS.FESTIVOS.BASE);
    
    if (response.data) {
      const festivosData = response.data.data || response.data.festivos || response.data;
      //console.log('✅ [FestivosService] Festivos obtenidos:', festivosData.length);
      return festivosData;
    } else {
      console.warn('⚠️ [FestivosService] No se encontraron festivos');
      return [];
    }
  } catch (error) {
    console.error('❌ [FestivosService] Error al obtener festivos:', error);
    throw error;
  }
};

/**
 * Obtiene un día festivo por ID
 * @param {number} festivoId - ID del día festivo
 * @returns {Promise<Object>} - Datos del día festivo
 */
export const getFestivoById = async (festivoId) => {
  try {
    //console.log('📅 [FestivosService] Obteniendo festivo por ID:', festivoId);
    
    const response = await apiClient.get(API_ENDPOINTS.FESTIVOS.BY_ID(festivoId));
    
    if (response.data) {
      const festivoData = response.data.data || response.data.festivo || response.data;
      //console.log('✅ [FestivosService] Festivo obtenido:', festivoData);
      return festivoData;
    } else {
      throw new Error('Festivo no encontrado');
    }
  } catch (error) {
    console.error('❌ [FestivosService] Error al obtener festivo:', error);
    throw error;
  }
};

/**
 * Obtiene días festivos por año
 * @param {number} year - Año a consultar
 * @returns {Promise<Array>} - Lista de días festivos del año
 */
export const getFestivosByYear = async (year) => {
  try {
    //console.log('📅 [FestivosService] Obteniendo festivos por año:', year);
    
    const response = await apiClient.get(API_ENDPOINTS.FESTIVOS.BY_YEAR(year));
    
    if (response.data) {
      const festivosData = response.data.data || response.data.festivos || response.data;
      //console.log('✅ [FestivosService] Festivos del año obtenidos:', festivosData.length);
      return festivosData;
    } else {
      return [];
    }
  } catch (error) {
    console.error('❌ [FestivosService] Error al obtener festivos por año:', error);
    throw error;
  }
};

/**
 * Obtiene días festivos en un rango de fechas
 * @param {string} fechaInicio - Fecha de inicio (YYYY-MM-DD)
 * @param {string} fechaFin - Fecha de fin (YYYY-MM-DD)
 * @returns {Promise<Array>} - Lista de días festivos en el rango
 */
export const getFestivosByDateRange = async (fechaInicio, fechaFin) => {
  try {
    //console.log('📅 [FestivosService] Obteniendo festivos por rango:', { fechaInicio, fechaFin });
    
    const response = await apiClient.get(API_ENDPOINTS.FESTIVOS.BY_DATE_RANGE, {
      params: {
        fecha_inicio: fechaInicio,
        fecha_fin: fechaFin
      }
    });
    
    if (response.data) {
      const festivosData = response.data.data || response.data.festivos || response.data;
      //console.log('✅ [FestivosService] Festivos en rango obtenidos:', festivosData.length);
      return festivosData;
    } else {
      return [];
    }
  } catch (error) {
    console.error('❌ [FestivosService] Error al obtener festivos por rango:', error);
    throw error;
  }
};

/**
 * Crea un nuevo día festivo
 * @param {Object} festivoData - Datos del día festivo
 * @returns {Promise<Object>} - Día festivo creado
 */
export const createFestivo = async (festivoData) => {
  try {
    //console.log('📅 [FestivosService] Creando festivo:', festivoData);
    
    const response = await apiClient.post(API_ENDPOINTS.FESTIVOS.BASE, festivoData);
    
    if (response.data) {
      const festivo = response.data.data || response.data.festivo || response.data;
      //console.log('✅ [FestivosService] Festivo creado:', festivo);
      return festivo;
    } else {
      throw new Error('Error al crear el festivo');
    }
  } catch (error) {
    console.error('❌ [FestivosService] Error al crear festivo:', error);
    throw error;
  }
};

/**
 * Actualiza un día festivo existente
 * @param {number} festivoId - ID del día festivo
 * @param {Object} festivoData - Datos actualizados del día festivo
 * @returns {Promise<Object>} - Día festivo actualizado
 */
export const updateFestivo = async (festivoId, festivoData) => {
  try {
    //console.log('📅 [FestivosService] Actualizando festivo:', { festivoId, festivoData });
    
    const response = await apiClient.put(API_ENDPOINTS.FESTIVOS.BY_ID(festivoId), festivoData);
    
    if (response.data) {
      const festivo = response.data.data || response.data.festivo || response.data;
      //console.log('✅ [FestivosService] Festivo actualizado:', festivo);
      return festivo;
    } else {
      throw new Error('Error al actualizar el festivo');
    }
  } catch (error) {
    console.error('❌ [FestivosService] Error al actualizar festivo:', error);
    throw error;
  }
};

/**
 * Elimina un día festivo
 * @param {number} festivoId - ID del día festivo
 * @returns {Promise<boolean>} - true si se eliminó correctamente
 */
export const deleteFestivo = async (festivoId) => {
  try {
    //console.log('📅 [FestivosService] Eliminando festivo:', festivoId);
    
    const response = await apiClient.delete(API_ENDPOINTS.FESTIVOS.BY_ID(festivoId));
    
    //console.log('✅ [FestivosService] Festivo eliminado correctamente');
    return true;
  } catch (error) {
    console.error('❌ [FestivosService] Error al eliminar festivo:', error);
    throw error;
  }
};

/**
 * Verifica si una fecha es festiva
 * @param {string} fecha - Fecha a verificar (YYYY-MM-DD)
 * @returns {Promise<boolean>} - true si es festiva
 */
export const isFechaFestiva = async (fecha) => {
  try {
    const festivos = await getFestivosByDateRange(fecha, fecha);
    return festivos.length > 0;
  } catch (error) {
    console.error('❌ [FestivosService] Error al verificar fecha festiva:', error);
    return false;
  }
};

/**
 * Obtiene información de festivo para una fecha específica
 * @param {string} fecha - Fecha a consultar (YYYY-MM-DD)
 * @returns {Promise<Object|null>} - Información del festivo o null si no es festiva
 */
export const getFestivoByFecha = async (fecha) => {
  try {
    const festivos = await getFestivosByDateRange(fecha, fecha);
    return festivos.length > 0 ? festivos[0] : null;
  } catch (error) {
    console.error('❌ [FestivosService] Error al obtener festivo por fecha:', error);
    return null;
  }
};

/**
 * Actualiza la política de festivos para una sucursal
 * @param {string} sucursalId - ID de la sucursal
 * @param {string} politica - Nueva política ('cerrar_automaticamente' | 'configuracion_individual')
 * @returns {Promise<Object>} - Resultado de la operación
 */
export const actualizarPoliticaFestivos = async (sucursalId, politica) => {
  try {
    //console.log('🏢 [FestivosService] Actualizando política de festivos:', { sucursalId, politica });
    
    const response = await apiClient.put(`${API_ENDPOINTS.FESTIVOS.BY_SUCURSAL(sucursalId)}/politica`, {
      politica
    });
    
    if (response.data) {
      //console.log('✅ [FestivosService] Política de festivos actualizada');
      return response.data;
    }
    
    return response;
  } catch (error) {
    console.error('❌ [FestivosService] Error al actualizar política de festivos:', error);
    throw error;
  }
};

/**
 * Configura el comportamiento de un festivo específico para una sucursal
 * @param {string} sucursalId - ID de la sucursal
 * @param {string} festivoId - ID del festivo
 * @param {Object} configuracion - Configuración del festivo
 * @param {string} configuracion.comportamiento - 'horario_normal' | 'cerrado' | 'horario_especial'
 * @param {Object} configuracion.horario_especial - Horario especial (opcional)
 * @returns {Promise<Object>} - Resultado de la operación
 */
export const configurarFestivoSucursal = async (sucursalId, festivoId, configuracion) => {
  try {
    //console.log('🎉 [FestivosService] Configurando festivo:', { sucursalId, festivoId, configuracion });
    
    const response = await apiClient.post(`${API_ENDPOINTS.FESTIVOS.BY_SUCURSAL(sucursalId)}/configurar`, {
      festivo_id: festivoId,
      ...configuracion
    });
    
    if (response.data) {
      //console.log('✅ [FestivosService] Festivo configurado');
      return response.data;
    }
    
    return response;
  } catch (error) {
    console.error('❌ [FestivosService] Error al configurar festivo:', error);
    throw error;
  }
};

/**
 * Obtiene la configuración de festivos para una sucursal
 * @param {string} sucursalId - ID de la sucursal
 * @returns {Promise<Object>} - Configuración de festivos de la sucursal
 */
export const getConfiguracionFestivosSucursal = async (sucursalId) => {
  try {
    //console.log('🏢 [FestivosService] Obteniendo configuración de festivos para sucursal:', sucursalId);
    
    const response = await apiClient.get(API_ENDPOINTS.FESTIVOS.BY_SUCURSAL(sucursalId));
    
    if (response.data) {
      const configuracionData = response.data.data || response.data;
      /*console.log('✅ [FestivosService] Configuración de festivos obtenida:', {
        estructuraOriginal: response.data,
        configuracionProcesada: configuracionData,
        cantidadFestivos: configuracionData.festivos?.length || 0
      });*/
      return configuracionData;
    } else {
      return {
        politica: 'configuracion_individual',
        festivos: []
      };
    }
  } catch (error) {
    console.error('❌ [FestivosService] Error al obtener configuración de festivos:', error);
    // Retornar configuración por defecto si hay error
    return {
      politica: 'configuracion_individual',
      festivos: []
    };
  }
};